import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { LocalisationProvider } from 'context/LocalisationProvider';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { QueryParamProvider } from 'use-query-params';
import { Toaster } from 'components/Toaster';
import { DeviceProvider } from 'context/DeviceProvider';
import { TenantConfigProvider } from 'context/TenantConfigProvider';
import { InfoDrawerProvider } from 'context/InfoDrawerContext';
import { InfoDrawer } from 'components/InfoDrawer/InfoDrawer';
import { CustomThemeProvider } from 'styles/configs/theme';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { persister, queryClient } from 'context/query';
import * as Sentry from '@sentry/react';
import { Auth0ProviderWithNavigate as Auth0ProviderWithRedirectCallback } from './context/Auth0ProviderWithRedirectCallback';
import { AuthenticatedApp } from './App';
import { runtimeConfig } from 'config/runtimeConfig';

import 'styles/main.css';
import { GenericErrorPage } from 'components/errorpages/GenericErrorPage';
import { ChunkLoadErrorFallback } from 'components/errorpages/ChunkLoadErrorFallback';
import { ChunkLoadRecoveryRoot } from 'components/errorpages/ChunkLoadRecoveryRoot';
import { installChunkLoadRecovery, isChunkLoadError } from 'utils/chunkLoadRecovery';

if (runtimeConfig.SENTRY_DSN) {
  Sentry.init({
    dsn: runtimeConfig.SENTRY_DSN,
    release: runtimeConfig.SENTRY_RELEASE,
    environment: runtimeConfig.ENV_NAME || 'development',
    tunnel: `${runtimeConfig.API_URL}/sentry/tunnel`,
    replaysOnErrorSampleRate: 1.0,
    integrations: [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })],
    ignoreErrors: ['cancelled', 'canceled', 'aborted', 'Network Error'],
  });
}

installChunkLoadRecovery(runtimeConfig.SENTRY_RELEASE || 'development');

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <CustomThemeProvider>
      <BrowserRouter>
        <Auth0ProviderWithRedirectCallback>
          <DeviceProvider>
            <QueryParamProvider adapter={ReactRouter6Adapter}>
              <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
                <LocalisationProvider>
                  <ChunkLoadRecoveryRoot>
                    <Sentry.ErrorBoundary
                      fallback={({ error }) =>
                        isChunkLoadError(error) ? (
                          <ChunkLoadErrorFallback release={runtimeConfig.SENTRY_RELEASE || 'development'} />
                        ) : (
                          <GenericErrorPage />
                        )
                      }
                    >
                      <TenantConfigProvider>
                        <InfoDrawerProvider>
                          <Toaster position="top-right" duration={2000} />
                          <AuthenticatedApp />
                          <InfoDrawer />
                        </InfoDrawerProvider>
                      </TenantConfigProvider>
                    </Sentry.ErrorBoundary>
                  </ChunkLoadRecoveryRoot>
                </LocalisationProvider>
              </PersistQueryClientProvider>
            </QueryParamProvider>
          </DeviceProvider>
        </Auth0ProviderWithRedirectCallback>
      </BrowserRouter>
    </CustomThemeProvider>
  </React.StrictMode>
);
